import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GuestTabletWorkstationSelect } from "@/components/guest-tablet/GuestTabletWorkstationSelect";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  readGuestTabletWorkstationCode,
  writeGuestTabletWorkstationCode,
} from "@/lib/guestTabletWorkstationStorage";
import {
  POST_LOGIN_HOME,
  buildEntraStartUrl,
  checkAuthenticated,
  isElectronLoginRuntime,
  parseEntraLoginError,
} from "@/lib/entraLogin";
import { api } from "@/lib/apiClient";
import type { CurrentUser } from "@/lib/auth";
import { isAppUser } from "@/lib/acl";
import { loadUserUiPreferences } from "@/lib/userUiPreferences";
import { BrandingLogo } from "@/components/branding/BrandingLogo";
import { useBranding } from "@/lib/branding/useBranding";
import { LogoutNotice } from "@/pages/auth/LogoutNotice";

export function LoginPage() {
  const { siteName } = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logoutFlow = searchParams.get("logout") === "1";

  const [phase, setPhase] = useState<"checking" | "ready">("checking");
  const [error, setError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginHint, setLoginHint] = useState("Mit Microsoft anmelden");
  const [logoutCountdown, setLogoutCountdown] = useState(6);
  const [guestTabletWorkstation, setGuestTabletWorkstation] = useState<string | null>(() =>
    readGuestTabletWorkstationCode(),
  );

  useEffect(() => {
    const loginError = parseEntraLoginError(searchParams.toString());
    if (loginError) setError(loginError);
  }, [searchParams]);

  useEffect(() => {
    if (!logoutFlow) return;
    setLogoutCountdown(6);
    const timer = window.setInterval(() => {
      setLogoutCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          navigate("/login", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [logoutFlow, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (logoutFlow) {
        if (!cancelled) setPhase("ready");
        return;
      }
      const authed = await checkAuthenticated();
      if (cancelled) return;
      if (authed) {
        let homePage = POST_LOGIN_HOME;
        try {
        const user = await api.get<CurrentUser>("/me");
        const accessGroups =
          Array.isArray(user.groups_real) && user.groups_real.length > 0
            ? user.groups_real
            : user.groups ?? [];
        if (!isAppUser(accessGroups)) {
          navigate("/kein-zugang", { replace: true });
          return;
        }
        homePage = (await loadUserUiPreferences(user)).home_page;
        } catch {
          /* fallback to dashboard */
        }
        navigate(homePage, { replace: true });
        return;
      }
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [logoutFlow, navigate]);

  async function handleEntraLogin() {
    const startUrl = buildEntraStartUrl(POST_LOGIN_HOME);
    if (isElectronLoginRuntime()) {
      setLoginLoading(true);
      try {
        const ok = await window.envAPI!.openExternalLogin!(startUrl);
        if (!ok) {
          setLoginLoading(false);
          setError("Login konnte im Browser nicht geöffnet werden.");
          return;
        }
        setLoginHint("Bitte im Browser anmelden – Fenster bleibt offen …");
      } catch (err) {
        setLoginLoading(false);
        setError("Login konnte im Browser nicht gestartet werden.");
        console.error("openExternalLogin failed", err);
      }
      return;
    }

    setLoginLoading(true);
    window.location.href = startUrl;
  }

  function skipLogoutCountdown() {
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-grouped px-4 py-8 text-label">
      <main
        className={`w-full rounded-ios-lg bg-card shadow-ios-md ${
          logoutFlow ? "max-w-[420px] p-8 sm:p-10" : "max-w-[360px] p-6 sm:p-8"
        }`}
      >
        <header className={`text-center ${logoutFlow ? "mb-2" : "mb-6"}`}>
          <div className="mx-auto mb-4 flex h-14 items-center justify-center">
            <BrandingLogo
              imgClassName="max-h-14 max-w-[12rem]"
              fallbackClassName="flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-2xl"
            />
          </div>
          {!logoutFlow ? (
            <>
              <h1 className="text-xl font-semibold text-label">{siteName}</h1>
              <p className="mt-1 text-sm text-label-secondary">
                Bitte melde dich mit deinem Microsoft-Konto an.
              </p>
            </>
          ) : null}
        </header>

        {error ? (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        ) : null}

        {logoutFlow ? (
          <LogoutNotice countdown={logoutCountdown} onContinue={skipLogoutCountdown} />
        ) : null}

        {phase === "checking" ? (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-label-secondary">
            <Spinner />
            <span>Session wird geprüft …</span>
          </div>
        ) : null}

        {phase === "ready" && !logoutFlow ? (
          <div className="space-y-4">
            <GuestTabletWorkstationSelect
              publicMode
              value={guestTabletWorkstation}
              onChange={(code) => {
                setGuestTabletWorkstation(code);
                writeGuestTabletWorkstationCode(code);
              }}
              className="flex w-full flex-col items-stretch gap-1.5 text-sm"
            />
            <p className="text-xs text-label-tertiary">
              Optional: Gäste-Tablet für den Check-in an dieser Rezeption. Die Auswahl bleibt nach dem
              Anmelden erhalten.
            </p>
            <Button
              type="button"
              className="w-full"
              size="lg"
              loading={loginLoading}
              onClick={() => void handleEntraLogin()}
            >
              {loginHint}
            </Button>
          </div>
        ) : null}
      </main>
    </div>
  );
}
